import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives";

import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";

import type { DataEventProps } from "../../../../wpl/foundations/src/index";

export interface BreadcrumbsProps {
    links: {
        name: string;
        path: string;
    }[];
    dataEventProps?: DataEventProps;
}

export interface BreadcrumbProps {
    link: string;
    name: string;
    dataEventProps?: DataEventProps;
}

const breadcrumbLink = css({
    ":hover": {
        textDecoration: "underline"
    }
});

const breadcrumbsUnorderedList = css({
    display: "flex",
    listStyle: "none",
    padding: "0px"
});

const breadcrumbsFirstItem = css({
    "color": wacToken("color.extended.blue900"),
    "fontSize": "14px",
    "::after": {
        content: "/",
        display: "inline-block",
        color: wacToken("color.neutral.light600"),
        margin: "0px 4px 0px 4px"
    },
    [media.above.sm]: {
        fontSize: "16px"
    }
});

const breadcrumbsInnerListItem = css({
    display: "none",
    [media.above.sm]: {
        "display": "inline",
        "color": wacToken("color.extended.blue900"),
        "::after": {
            content: "/",
            display: "inline-block",
            color: wacToken("color.neutral.light600"),
            margin: "0px 4px 0px 4px"
        }
    }
});

const breadcrumbsLastItem = css({
    color: wacToken("color.neutral.light600"),
    fontSize: "14px",
    [media.above.sm]: {
        fontSize: "16px"
    }
});

const Breadcrumb = ({ link, name, dataEventProps }: BreadcrumbProps) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const handleClick = () => {
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            actionSubject: "linkButton",
            actionSubjectId: "breadcrumb",
            source: "breadcrumbs",
            attributes: { label: name }
        }).fire();
    };

    return (
        <a
            css={breadcrumbLink}
            href={useLocalizedHref(link)}
            onClick={handleClick}
            {...dataEventProps}
        >
            {name}
        </a>
    );
};

export const Breadcrumbs = ({ links, dataEventProps }: BreadcrumbsProps) => {
    return (
        <>
            <ul css={breadcrumbsUnorderedList}>
                {links.map((link, index) => {
                    if (index === 0) {
                        return (
                            <li css={breadcrumbsFirstItem} key={index}>
                                <Breadcrumb
                                    name={link.name}
                                    link={link.path}
                                    dataEventProps={dataEventProps}
                                />
                            </li>
                        );
                    } else if (index + 1 === links.length) {
                        return (
                            <li css={breadcrumbsLastItem} key={index}>
                                <Breadcrumb
                                    name={link.name}
                                    link={link.path}
                                    dataEventProps={dataEventProps}
                                />
                            </li>
                        );
                    } else {
                        return (
                            <li css={breadcrumbsInnerListItem} key={index}>
                                <Breadcrumb
                                    name={link.name}
                                    link={link.path}
                                    dataEventProps={dataEventProps}
                                />
                            </li>
                        );
                    }
                })}
            </ul>
        </>
    );
};
